import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { USER_PROFILE_FIELDS, USERS_TABLE } from "@/lib/userAccess";
import { isAdminRole, ADMIN_ACCESS_DENIED_MESSAGE } from "@/lib/appBranding";

const AuthContext = createContext(null);

const SESSION_TIMEOUT_MS = 10000;
const PROFILE_TIMEOUT_MS = 8000;
const INACTIVE_USER_MESSAGE = "Este usuario está dado de baja. Contacta con el administrador.";

const isInactiveUser = (userData) => userData?.active === false;

const withTimeout = async (promise, ms, label = "operación") => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`timeout:${label}`)), ms);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

async function fetchUserProfile(authUserId) {
  const { data: userData, error } = await withTimeout(
    supabase.from(USERS_TABLE).select(USER_PROFILE_FIELDS).eq("id", authUserId).single(),
    PROFILE_TIMEOUT_MS,
    "perfil"
  );
  if (error) throw error;
  return userData;
}

async function signOutSafe() {
  try {
    await withTimeout(supabase.auth.signOut(), 5000, "signOut");
  } catch (e) {
    console.warn("signOut:", e?.message || e);
  }
}

async function resolveAdminUser(authUserId, fallbackUsername = null) {
  let userData = null;

  try {
    userData = await fetchUserProfile(authUserId);
  } catch (profileError) {
    if (!fallbackUsername) throw profileError;

    const { data: byEmailUser, error: byEmailError } = await withTimeout(
      supabase
        .from(USERS_TABLE)
        .select(USER_PROFILE_FIELDS)
        .ilike("username", fallbackUsername)
        .single(),
      PROFILE_TIMEOUT_MS,
      "perfil_username"
    );
    if (byEmailError || !byEmailUser) throw profileError;
    userData = byEmailUser;
  }

  if (isInactiveUser(userData)) {
    await signOutSafe();
    return { user: null, error: INACTIVE_USER_MESSAGE };
  }

  if (!isAdminRole(userData)) {
    await signOutSafe();
    return { user: null, error: ADMIN_ACCESS_DENIED_MESSAGE };
  }

  return { user: userData, error: null };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const finishLoading = () => {
      if (isMounted) setLoading(false);
    };

    const syncSession = async (session, fallbackUsername = null) => {
      if (!session?.user?.id) {
        setUser(null);
        return;
      }
      try {
        const { user: adminUser, error } = await resolveAdminUser(session.user.id, fallbackUsername);
        if (!isMounted) return;
        setUser(adminUser);
        if (error) console.warn(error);
      } catch (e) {
        console.warn("Error verificando sesión:", e?.message || e);
        if (!isMounted) return;
        await signOutSafe();
        setUser(null);
      }
    };

    const init = async () => {
      try {
        const {
          data: { session },
        } = await withTimeout(supabase.auth.getSession(), SESSION_TIMEOUT_MS, "getSession");
        if (!isMounted) return;
        await syncSession(session);
      } catch (e) {
        console.warn("Init auth:", e?.message || e);
        if (isMounted) setUser(null);
      } finally {
        finishLoading();
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Evitar deadlock: no hacer await de Supabase aquí (ver docs Supabase Auth)
      if (event === "INITIAL_SESSION") return;

      setTimeout(() => {
        if (!isMounted) return;

        if (event === "SIGNED_OUT") {
          setUser(null);
          return;
        }

        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
          void syncSession(session);
        }
      }, 0);
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  const login = async (username, password) => {
    try {
      const normalizedUsername = username.trim().toLowerCase();

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedUsername,
        password,
      });
      if (error) throw error;

      const authUserId = data?.user?.id;
      if (!authUserId) return { success: false, error: "No se obtuvo ID de usuario" };

      const { user: adminUser, error: accessError } = await resolveAdminUser(authUserId, normalizedUsername);
      if (accessError || !adminUser) {
        return { success: false, error: accessError || ADMIN_ACCESS_DENIED_MESSAGE };
      }

      setUser(adminUser);
      return { success: true };
    } catch (error) {
      console.error("Error logging in:", error);
      return { success: false, error: error?.message || "Error desconocido" };
    }
  };

  const logout = async () => {
    try {
      await signOutSafe();
      setUser(null);
    } catch (error) {
      console.error("Error logging out:", error);
      setUser(null);
    }
  };

  const hasPermission = () => {
    if (!user || !isAdminRole(user)) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Cargando...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
